import React, { useEffect, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  MapPin, Home, Briefcase, DollarSign, Users, Menu, Gavel, Calculator, ClipboardCheck, Shield,
} from 'lucide-react';
import { LegalplusLogo, LegalplusMark } from './brand/LegalplusLogo';

const Sidebar = ({ sidebarOpen, toggleSidebar, activeSection, setActiveSection, userRole, user, permisos }) => {
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const isExpanded = sidebarOpen || isHovered;

  // Mapa de módulos a items del menú
  const MODULO_MENU_MAP = [
    { modulo: 'dashboard',    name: 'Dashboard',    icon: Home,           section: 'dashboard',    path: '/' },
    { modulo: 'seguimiento',  name: 'Seguimiento',  icon: Gavel,          section: 'seguimiento',  path: '/seguimiento' },
    { modulo: 'casos',        name: 'Casos',        icon: Briefcase,      section: 'cases',        path: '/casos' },
    { modulo: 'facturacion',  name: 'Facturación',  icon: DollarSign,     section: 'invoices',     path: '/facturacion' },
    { modulo: 'clientes',     name: 'Clientes',     icon: Users,          section: 'clients',      path: '/clientes' },
    { modulo: 'cuenta_cobro', name: 'Abogados',     icon: Users,          section: 'abogados',     path: '/abogados' },
    { modulo: 'usuarios',     name: 'Usuarios',     icon: Users,          section: 'usuarios',     path: '/usuarios' },
    { modulo: 'encuestas',    name: 'Encuestas',    icon: ClipboardCheck, section: 'encuestas',    path: '/encuestas' },
    { modulo: 'contabilidad', name: 'Contabilidad', icon: Calculator,     section: 'contabilidad', path: '/contabilidad' },
  ];

  const roleNorm = (userRole || '').toLowerCase();
  const isAbogado = roleNorm === 'abogado' || roleNorm === 'abogado_seguimiento';

  const filteredMenuItems = useMemo(() => {
    if (!permisos?.modulos) {
      return [{ name: 'Dashboard', icon: Home, section: 'dashboard', path: '/' }];
    }
    if (isAbogado) {
      return [
        { name: 'Mi Panel',         icon: Home,   section: 'dashboard',  path: '/' },
        { name: 'Geolocalización',  icon: MapPin, section: 'geolocation', path: '/abogado-login' },
      ];
    }
    const items = MODULO_MENU_MAP
      .filter(item => permisos.modulos[item.modulo]?.ver)
      .map(({ modulo, ...rest }) => rest);

    if (permisos.modulos.usuarios?.ver) {
      const usrIdx = items.findIndex(i => i.section === 'usuarios');
      if (usrIdx >= 0) {
        items.splice(usrIdx + 1, 0, { name: 'Roles', icon: Shield, section: 'roles', path: '/roles' });
      }
    }
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permisos, isAbogado]);

  useEffect(() => {
    const currentPath = location.pathname;
    const activeItem = filteredMenuItems.find(item => item.path === currentPath);
    if (activeItem) setActiveSection(activeItem.section);
  }, [location.pathname, filteredMenuItems, setActiveSection]);

  const handleLinkClick = (section) => {
    setActiveSection(section);
    if (window.innerWidth < 1024 && sidebarOpen) toggleSidebar();
    setIsHovered(false);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.trim().split(' ').filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase();
  };

  return (
    <>
      <aside
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          fixed top-0 h-full z-50 flex flex-col
          transition-all duration-[400ms] ease-brand
          text-brand-silver
          ${isExpanded ? 'w-64' : 'w-[74px]'}
          ${sidebarOpen ? 'left-0' : '-left-64 lg:left-0'}
        `}
        style={{
          background: 'linear-gradient(180deg, #050B18 0%, #0A1628 60%, #0A1628 100%)',
          borderRight: '1px solid rgba(79, 125, 255, 0.08)',
          boxShadow: '24px 0 60px -24px rgba(0,0,0,0.5)',
        }}
      >
        {/* Hairline luminosa vertical */}
        <div
          aria-hidden="true"
          className="absolute top-0 right-0 w-px h-full pointer-events-none"
          style={{
            background: 'linear-gradient(180deg, transparent 0%, rgba(79,125,255,0.22) 30%, rgba(79,125,255,0.22) 70%, transparent 100%)',
          }}
        />

        {/* ═══ LOGO ═══ */}
        <div className="px-4 pt-5 pb-5 border-b border-white/[0.05]">
          <div className="flex items-center justify-between min-h-[40px]">
            {isExpanded ? (
              <div className="overflow-hidden animate-fadeIn">
                <LegalplusLogo variant="full" tone="light" size="sm" />
              </div>
            ) : (
              <div className="mx-auto">
                <LegalplusMark size={36} tone="light" />
              </div>
            )}

            {isExpanded && (
              <button
                onClick={toggleSidebar}
                className="text-brand-silver-mute p-1.5 rounded-lg hover:bg-white/[0.05] hover:text-brand-silver transition-all duration-200 flex-shrink-0"
                aria-label="Colapsar sidebar"
              >
                <Menu size={18} />
              </button>
            )}
          </div>
        </div>

        {/* ═══ NAV ═══ */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto custom-scrollbar">
          {isExpanded && (
            <p className="eyebrow text-brand-silver-mute/70 px-3 mb-2.5">Operaciones</p>
          )}
          <ul className="space-y-1">
            {filteredMenuItems.map((item) => {
              const isActive = activeSection === item.section;
              return (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    onClick={() => handleLinkClick(item.section)}
                    title={!isExpanded ? item.name : undefined}
                    className={`
                      relative flex items-center rounded-brand transition-all duration-[280ms] ease-brand group
                      ${isExpanded ? 'px-3 py-2.5' : 'p-2.5 justify-center'}
                      ${isActive
                        ? 'text-brand-silver bg-brand-indigo/10'
                        : 'text-brand-silver-dim hover:text-brand-silver hover:bg-white/[0.03]'
                      }
                    `}
                  >
                    {/* Indicador activo — barra luminosa */}
                    {isActive && (
                      <span
                        className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 rounded-r-full"
                        style={{
                          background: 'linear-gradient(180deg, #7DA1FF 0%, #4F7DFF 100%)',
                          boxShadow: '0 0 14px rgba(79, 125, 255, 0.6)',
                        }}
                      />
                    )}

                    <item.icon
                      size={18}
                      className={`
                        flex-shrink-0 transition-all duration-[280ms] ease-brand
                        ${isExpanded ? 'mr-3' : ''}
                        ${isActive
                          ? 'text-brand-indigo-soft'
                          : 'group-hover:text-brand-silver group-hover:scale-[1.08]'
                        }
                      `}
                      strokeWidth={isActive ? 2.2 : 1.8}
                    />

                    <span
                      className={`
                        whitespace-nowrap text-[13px] font-medium tracking-[0.01em] transition-all duration-300
                        ${isExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0 overflow-hidden'}
                      `}
                    >
                      {item.name}
                    </span>

                    {isExpanded && isActive && (
                      <span
                        className="ml-auto w-1.5 h-1.5 rounded-full bg-brand-cyan"
                        style={{ boxShadow: '0 0 8px rgba(92, 225, 230, 0.8)' }}
                      />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* ═══ FOOTER — usuario ═══ */}
        <div className="px-3 pb-4 pt-3 border-t border-white/[0.05] flex-shrink-0">
          {isExpanded ? (
            <div className="flex items-center gap-3 px-2 py-2 rounded-brand hover:bg-white/[0.03] transition-all duration-200">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0 shadow-md relative"
                style={{
                  background: 'linear-gradient(135deg, #4F7DFF 0%, #2B4EB8 100%)',
                  boxShadow: '0 0 0 1px rgba(79,125,255,0.3), 0 8px 20px -8px rgba(79,125,255,0.5)',
                }}
              >
                {getInitials(user?.nombre)}
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-ok-DEFAULT border-2 border-brand-night" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-semibold text-brand-silver truncate leading-none">
                  {user?.nombre || 'Usuario'}
                </p>
                <p className="mt-1 text-[9px] tracking-[0.22em] font-semibold text-brand-silver-mute uppercase leading-none">
                  {userRole || 'Rol'}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[11px] font-bold relative"
                style={{
                  background: 'linear-gradient(135deg, #4F7DFF 0%, #2B4EB8 100%)',
                  boxShadow: '0 0 0 1px rgba(79,125,255,0.3)',
                }}
              >
                {getInitials(user?.nombre)}
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-ok-DEFAULT border-2 border-brand-night" />
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Overlay móvil */}
      {sidebarOpen && (
        <div
          onClick={toggleSidebar}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 lg:hidden transition-all animate-fadeIn"
        />
      )}
    </>
  );
};

export default Sidebar;
